/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React from 'react'
import { useServiceContext } from '../../../root'
import type { FolderStructureTemplateType } from '../../../services/api/api.folderstructuretemplatetypes'
import { createDateTimeFormatter } from '../../../utils/commonColumns'
import TableMenu from './TableMenu'
import { RcmTable } from '../../../components/rcm-table/RcmTable'
import { TableHeadRow, TableHeadCell, TableRow } from '../../../components/table/TableComponents'

interface FolderStrctTplTypeTableProps {
  onEditFolderStrctTplType: (folderStructureTemplateType: FolderStructureTemplateType) => void
  onDeleteSuccess?: () => void
  onExportStart?: () => void
  onExportEnd?: () => void
}

const Table: React.FC<FolderStrctTplTypeTableProps> = ({
  onEditFolderStrctTplType,
  onDeleteSuccess,
  onExportStart,
  onExportEnd
}) => {
  const context = useServiceContext()
  const { translate } = context.service.filter

  const formatDateTime = createDateTimeFormatter(context.service.auth, translate)

  const handleRowClick = (row: FolderStructureTemplateType) => {
    if (!row.system) {
      onEditFolderStrctTplType(row)
    }
  }

  return (
    <RcmTable
      className="chita-table"
      noResultsText={translate('NO_RESULTS_FILTERED')}
      HeadRow={
        <TableHeadRow className="has-actions">
          <TableHeadCell style={{ fontSize: '12px' }} sort="NAME">
            {translate('NAME')}
          </TableHeadCell>
          <TableHeadCell style={{ fontSize: '12px' }} id="63333dad-ad7b-4152-8023-ffda549589cb">
            {translate('DESCRIPTION')}
          </TableHeadCell>
          <TableHeadCell style={{ fontSize: '12px' }} sort="CREATED_AT">
            {translate('CREATED_ON')}
          </TableHeadCell>
          <TableHeadCell style={{ fontSize: '12px' }} sort="CREATED_BY">
            {translate('CREATED_BY')}
          </TableHeadCell>
          <TableHeadCell style={{ fontSize: '12px' }} sort="UPDATED_AT">
            {translate('TABLE_TH_MODIFIED_ON')}
          </TableHeadCell>
          <TableHeadCell style={{ fontSize: '12px' }} sort="UPDATED_BY">
            {translate('TABLE_TH_MODIFIED_BY')}
          </TableHeadCell>
          <TableHeadCell style={{ fontSize: '12px' }} id="da725586-a6a7-462a-bc3f-802dbfa9cc2c" className="actions">
            {translate('ACTIONS')}
          </TableHeadCell>
        </TableHeadRow>
      }
      Row={({ row }: { row: FolderStructureTemplateType }) => {
        return (
          <TableRow key={row.id}>
            <td className="clipped-cell">
              <div className="ellipsis">
                {row.system ? (
                  row.name
                ) : (
                  <span className="btn-link" onClick={() => handleRowClick(row)} style={{ cursor: 'pointer' }}>
                    {row.name}
                  </span>
                )}
              </div>
            </td>
            <td>{row.description || translate('NOT_AVAILABLE')}</td>
            <td>{formatDateTime(row.createdAt)}</td>
            <td>{row.createdBy && row.createdBy.name ? row.createdBy.name : translate('NOT_AVAILABLE')}</td>
            <td>{formatDateTime(row.updatedAt)}</td>
            <td>{row.updatedBy && row.updatedBy.name ? row.updatedBy.name : translate('NOT_AVAILABLE')}</td>
            <td className="actions">
              <TableMenu
                item={row}
                onEditFolderStrctTplType={onEditFolderStrctTplType}
                onDeleteSuccess={onDeleteSuccess}
                onExportStart={onExportStart}
                onExportEnd={onExportEnd}
              />
            </td>
          </TableRow>
        )
      }}
    />
  )
}

export default Table
